import { useState } from "react";
import ReferenceForm from "./ReferenceForm";
import ReferenceLink from "./ReferenceLink";
import { deleteGhgReference } from "./referencesApi";

/**
 * The reference library: add, edit, and delete one nothing cites. Every
 * change refreshes the library, for its usage counts, and the caller.
 *
 * @param {object} props
 * @param {import("./referencesApi").GhgReference[]} props.references
 * @param {string} props.error
 * @param {() => void} props.onReferencesChanged reloads the library and whatever shows references
 */
export default function ReferencesPanel({ references, error, onReferencesChanged }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const afterSave = () => {
    setAdding(false);
    setEditingId(null);
    onReferencesChanged();
  };

  const remove = async (reference) => {
    if (!window.confirm(`Delete the reference "${reference.title}"?`)) return;
    setBusyId(reference.id);
    const res = await deleteGhgReference(reference.id);
    setBusyId(null);
    if (res?.isSuccess) onReferencesChanged();
  };

  return (
    <section className="kgb-card efb-refs">
      <button type="button" className="efb-refs-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="efb-kpi-caret" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="efb-refs-title">References</span>
        <span className="efb-muted">
          {references.length} in the library · where factors, GWPs, NCVs, densities and databases are quoted from
        </span>
      </button>

      {open ? (
        <div className="efb-refs-body">
          {error ? <p className="efb-filter-note">{error}</p> : null}

          {adding ? (
            <ReferenceForm onSaved={afterSave} onCancel={() => setAdding(false)} />
          ) : (
            <button type="button" className="kgb-btn" onClick={() => setAdding(true)}>
              + New reference
            </button>
          )}
          {references.length ? (
            <table className="efb-table efb-reftable">
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Where in it</th>
                  <th scope="col" className="efb-num">
                    Cited by
                  </th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {references.map((reference) => {
                  const energyUses = reference.energy_factor_count + reference.energy_source_count;
                  const used = reference.factor_count + reference.database_count + energyUses;
                  if (editingId === reference.id) {
                    return (
                      <tr key={reference.id}>
                        <td colSpan={4}>
                          <ReferenceForm reference={reference} onSaved={afterSave} onCancel={() => setEditingId(null)} />
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={reference.id}>
                      <td>
                        <ReferenceLink reference={reference} />
                      </td>
                      <td className="efb-muted">{reference.note || "—"}</td>
                      <td className="efb-num">
                        {reference.factor_count.toLocaleString("en-IN")} factors · {reference.database_count} DB
                        {energyUses ? ` · ${energyUses} energy` : ""}
                      </td>
                      <td className="efb-refactions">
                        <button type="button" className="kgb-btn kgb-btn--quiet" onClick={() => setEditingId(reference.id)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="kgb-btn kgb-btn--quiet kgb-btn--danger"
                          disabled={used > 0 || busyId === reference.id}
                          title={used > 0 ? "Remove it from every factor, database and energy source first" : undefined}
                          onClick={() => remove(reference)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="efb-muted">No references yet. Add one, then apply it to factors below.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
